/**
 * Predicates that decide whether a GTM page_view should be pushed.
 *
 * Mail-security link scanners request 404 URLs whose last path segment is
 * exactly ten base64 characters, the truncated encoding of a real slug
 * (`/RXh4YWN0LV`, `/blog/hpc/Y29tcGFyaW`, `/category/TlZJRElBLU`). They execute
 * our JavaScript, so each fetch became a GA4 session, user and view. We drop
 * the page_view only when BOTH hold:
 *
 *   1. the router fell through to the `**` catch-all, and
 *   2. the last segment looks mangled.
 *
 * Both are required. `/category/Projectors` is a live page whose slug is ten
 * alphanumerics, but it resolves to the category route, never the catch-all.
 * And failed product/category/blog lookups call
 * `router.navigate(['/404'], { skipLocationChange: true })`, which matches the
 * `404` route, not `**`, so genuine broken links keep counting.
 *
 * Detection is by route snapshot rather than URL: for a true catch-all hit the
 * URL carries nothing that says "not found".
 *
 * Pure functions with no Angular imports so they run under a node Jest env.
 */

/** Structural subset of ActivatedRouteSnapshot, so tests need no Router. */
export interface RouteSnapshotLike {
    routeConfig?: { path?: string } | null;
    firstChild?: RouteSnapshotLike | null;
}

const MANGLED_SEGMENT = /^[A-Za-z0-9+]{10}$/;

const lastSegmentOf = (pathname: string | null | undefined): string => {
    const segments = String(pathname || '')
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
};

/** True when any node from the root down the active chain is the `**` route. */
export function isCatchAllRoute(root: RouteSnapshotLike | null | undefined): boolean {
    for (let node = root; node; node = node.firstChild) {
        if (node.routeConfig?.path === '**') {
            return true;
        }
    }
    return false;
}

/** True when the final path segment is exactly ten base64-alphabet characters. */
export function hasMangledLastSegment(pathname: string | null | undefined): boolean {
    return MANGLED_SEGMENT.test(lastSegmentOf(pathname));
}

export function shouldSuppressPageView(
    root: RouteSnapshotLike | null | undefined,
    pathname: string | null | undefined
): boolean {
    return isCatchAllRoute(root) && hasMangledLastSegment(pathname);
}
